import translationKeys from "./translationKeys.json";
import styles from "./TournamentPageTabs.module.css";
import {
  ENTRY_FEE_PGA,
  ENTRY_FEE_WITH_TAX,
  INITIAL_PURSE,
} from "../../utils/constants";
import {
  getTotalPrize,
  isElevatedRound,
  isPossibleToJoin,
} from "../../utils/tournaments";
import { useUI } from "../../context/UIContext";
import { utcTimeLocal } from "../../utils/time";
import Cta from "../Cta/Cta";
import dayjs from "dayjs";

interface OverviewProps {
  startDate: string;
  endDate: string;
  courseName: string;
  playersCount: number;
  isFinalSeason1: boolean;
  tournamentName: string;
  tournamentSeason: number;
  isPGATournament: boolean;
  configurationInfo: { displayFee: string; paymentUrl: string }[];
  signUpStartTime: string;
  signUpEndTime: string;
}

const Overview = ({
  endDate,
  startDate,
  courseName,
  playersCount,
  isFinalSeason1,
  tournamentName,
  tournamentSeason,
  isPGATournament,
  configurationInfo,
  signUpStartTime,
  signUpEndTime,
}: OverviewProps) => {
  const { dispatch } = useUI();

  const signupUri =
    !!configurationInfo.length && configurationInfo[0].paymentUrl;

  const isElevated = isElevatedRound(tournamentSeason, tournamentName);
  const isFinalSeason2 =
    tournamentSeason === 2 && tournamentName?.toLowerCase()?.includes("final");
  const isFinalSeason3 =
    tournamentSeason === 3 && tournamentName?.toLowerCase()?.includes("final");

  const getEntryFee = () => {
    if (isPGATournament) {
      return ENTRY_FEE_PGA;
    }
    if (isFinalSeason1 || isFinalSeason2 || isFinalSeason3) {
      return 0;
    }
    return ENTRY_FEE_WITH_TAX;
  };

  const signupStart = dayjs(signUpStartTime);
  const signupEnd = dayjs(signUpEndTime);
  const currentDate = dayjs();

  return (
    <div className={styles.overviewWrapper}>
      {isPGATournament && (
        <div className={styles.overviewItem}>
          <div className={styles.overviewItemTitle}>NOTE:</div>
          <div className={styles.overviewItemValue}>
            <p>
              PGA OF AMERICA PROFESSIONALS ONLY —{" "}
              <a
                href="https://tm-short.me/L3UeUIJ"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.signupLink}
              >
                SIGN UP HERE
              </a>
            </p>
          </div>
        </div>
      )}
      <div className={styles.overviewItem}>
        <div className={styles.overviewItemTitle}>{translationKeys.date}:</div>
        <div className={styles.overviewItemValue}>
          {utcTimeLocal(startDate, "MMM. D, H:mm z")} -{" "}
          {utcTimeLocal(endDate, "MMM. D, H:mm z, YYYY")}
        </div>
      </div>
      <div className={styles.overviewItem}>
        <div className={styles.overviewItemTitle}>
          {translationKeys["entry-fee"]}:
        </div>
        <div className={styles.overviewItemValue}>${getEntryFee()}</div>
      </div>
      <div className={styles.overviewItem}>
        <div className={styles.overviewItemTitle}>
          {translationKeys.course}:
        </div>
        <div className={styles.overviewItemValue}>{courseName}</div>
      </div>
      <div className={styles.overviewItem}>
        <div className={styles.overviewItemTitle}>{translationKeys.purse}:</div>
        <div className={styles.overviewItemValue}>
          $
          {isFinalSeason1
            ? INITIAL_PURSE.toLocaleString("en-US")
            : getTotalPrize(
                playersCount,
                isElevated,
                isFinalSeason2,
                isPGATournament,
                isFinalSeason3
              ).toLocaleString("en-US")}
        </div>
      </div>
      <div className={styles.overviewItem}>
        <div className={styles.overviewItemTitle}>
          {translationKeys.players}:
        </div>
        <div className={styles.overviewItemValue}>
          {tournamentSeason === 1
            ? playersCount
            : isFinalSeason2
            ? 100
            : `${playersCount} / 1000`}
        </div>
      </div>
      {signUpStartTime &&
        signUpEndTime &&
        currentDate.isAfter(signupStart) &&
        currentDate.isBefore(signupEnd) &&
        isPossibleToJoin(playersCount) &&
        signupUri &&
        !isPGATournament &&
        !isFinalSeason3 && (
          <Cta
            name={translationKeys["join-tournament"]}
            className={styles.overviewJoinButton}
            onClick={() => {
              window.localStorage.setItem("next_signup_uri", signupUri);
              dispatch({
                type: "toggleJoinFlowInfoModal",
                payload: {
                  isOpen: true,
                  title: tournamentName,
                  modalType: "signup",
                },
              });
            }}
          />
        )}
    </div>
  );
};

export default Overview;
